import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./DataAnalytics.css";
import Testimonial from "../../../Testimonial/Testimonial";

const DataAnalytics = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>
          Data Analytics Solutions | Advanced Business Intelligence Tools
        </title>
        <meta
          name="description"
          content="Comprehensive data analytics services leveraging AI, machine learning, and big data technologies to transform raw data into actionable business insights and predictive analytics."
        />
        <meta
          name="keywords"
          content="data analytics, business intelligence, big data, data visualization, predictive analytics, data mining, machine learning, AI analytics, data science, data engineering, data warehousing, ETL, data integration, data governance, data quality, data modeling, statistical analysis, dashboard reporting, KPI tracking, real-time analytics, prescriptive analytics, descriptive analytics, data storytelling, data lakes, data pipelines, cloud analytics, SQL analytics, NoSQL analytics, Python data analysis, R programming, Tableau, Power BI, Looker, Qlik, SAS, SPSS, Hadoop, Spark, Databricks, Snowflake, Redshift, BigQuery, data strategy, data-driven decision making, customer analytics, marketing analytics, financial analytics, operational analytics, risk analytics, sentiment analysis, text analytics, IoT analytics, streaming analytics, edge analytics, augmented analytics, automated ML, data ops, analytics as a service"
        />
      </Helmet>

      <div className="mk-data-analytics">
        <div className="mk-da-hero">
          <div className="mk-da-hero-content">
            <h1 className="mk-da-title">Data Analytics Services</h1>
            <p className="mk-da-subtitle">
              Transform Your Data Into Actionable Insights
            </p>
            <a href="/contact-us"><button className="mk-da-cta-btn">Get Started Today</button></a>
          </div>
          <div className="mk-da-hero-visual">
            <div className="mk-da-circle-graph"></div>
            <div className="mk-da-bar-chart"></div>
            <div className="mk-da-data-points"></div>
          </div>
        </div>

        <section className="mk-da-services-section">
          <h2 className="mk-da-section-title">Our Data Analytics Solutions</h2>
          <p className="mk-da-section-description">
            We provide cutting-edge data analytics services that transform your
            raw data into actionable insights, empowering your business to make
            informed decisions and stay ahead of the competition.
          </p>

          <div className="mk-da-services-grid">
            <div className="mk-da-service-card">
              <div className="mk-da-service-icon-container">
                <div className="mk-da-service-icon">
                  <div className="mk-da-icon-dashboard"></div>
                </div>
              </div>
              <h3>Business Intelligence</h3>
              <p>
                Interactive dashboards and reports that provide real-time
                insights into your business performance.
              </p>
              <ul className="mk-da-service-features">
                <li>Custom KPI dashboards</li>
                <li>Real-time reporting</li>
                <li>Performance metrics</li>
              </ul>
              <button className="mk-da-service-btn">Learn More</button>
            </div>

            <div className="mk-da-service-card">
              <div className="mk-da-service-icon-container">
                <div className="mk-da-service-icon">
                  <div className="mk-da-icon-predictive"></div>
                </div>
              </div>
              <h3>Predictive Analytics</h3>
              <p>
                Leverage historical data to forecast trends and make proactive
                business decisions.
              </p>
              <ul className="mk-da-service-features">
                <li>Trend forecasting</li>
                <li>Predictive modeling</li>
                <li>Scenario analysis</li>
              </ul>
              <button className="mk-da-service-btn">Learn More</button>
            </div>

            <div className="mk-da-service-card">
              <div className="mk-da-service-icon-container">
                <div className="mk-da-service-icon">
                  <div className="mk-da-icon-bigdata"></div>
                </div>
              </div>
              <h3>Big Data Solutions</h3>
              <p>
                Process and analyze massive datasets to uncover hidden patterns
                and correlations.
              </p>
              <ul className="mk-da-service-features">
                <li>Data processing pipelines</li>
                <li>Pattern recognition</li>
                <li>Scalable infrastructure</li>
              </ul>
              <button className="mk-da-service-btn">Learn More</button>
            </div>

            <div className="mk-da-service-card">
              <div className="mk-da-service-icon-container">
                <div className="mk-da-service-icon">
                  <div className="mk-da-icon-visualization"></div>
                </div>
              </div>
              <h3>Data Visualization</h3>
              <p>
                Transform complex data into intuitive visual representations for
                better understanding.
              </p>
              <ul className="mk-da-service-features">
                <li>Interactive charts</li>
                <li>Custom visualizations</li>
                <li>Data storytelling</li>
              </ul>
              <button className="mk-da-service-btn">Learn More</button>
            </div>

            <div className="mk-da-service-card">
              <div className="mk-da-service-icon-container">
                <div className="mk-da-service-icon">
                  <div className="mk-da-icon-machinelearning"></div>
                </div>
              </div>
              <h3>Machine Learning</h3>
              <p>
                Implement advanced ML algorithms to automate processes and
                extract deeper insights.
              </p>
              <ul className="mk-da-service-features">
                <li>Algorithm development</li>
                <li>Model training & deployment</li>
                <li>Automated insights</li>
              </ul>
              <button className="mk-da-service-btn">Learn More</button>
            </div>

            <div className="mk-da-service-card">
              <div className="mk-da-service-icon-container">
                <div className="mk-da-service-icon">
                  <div className="mk-da-icon-consulting"></div>
                </div>
              </div>
              <h3>Data Strategy Consulting</h3>
              <p>
                Develop comprehensive data strategies aligned with your business
                objectives.
              </p>
              <ul className="mk-da-service-features">
                <li>Data governance</li>
                <li>Strategy development</li>
                <li>Implementation planning</li>
              </ul>
              <button className="mk-da-service-btn">Learn More</button>
            </div>
          </div>
        </section>

        <section className="mk-da-process-section">
          <h2 className="mk-da-section-title">Our Analytics Process</h2>
          <div className="mk-da-process-steps">
            <div className="mk-da-process-step">
              <div className="mk-da-step-number"></div>
              <h3>Data Collection</h3>
              <p>
                Gathering data from multiple sources and ensuring its quality
                and integrity.
              </p>
            </div>
            <div className="mk-da-process-step">
              <div className="mk-da-step-number"></div>
              <h3>Data Processing</h3>
              <p>Cleaning, transforming, and organizing data for analysis.</p>
            </div>
            <div className="mk-da-process-step">
              <div className="mk-da-step-number"></div>
              <h3>Analysis & Modeling</h3>
              <p>
                Applying statistical techniques and algorithms to extract
                insights.
              </p>
            </div>
            <div className="mk-da-process-step">
              <div className="mk-da-step-number"></div>
              <h3>Visualization & Reporting</h3>
              <p>
                Presenting findings in an easily digestible format for
                decision-makers.
              </p>
            </div>
          </div>
        </section>

    <section className="mk-da-benefits-section">
      <h2 className="mk-da-section-title">
        Benefits of Our Data Analytics
      </h2>
      <div className="mk-da-benefits-container">
        <div className="mk-da-benefit-item">
          <h3>Improved Decision Making</h3>
          <p>
            Make data-driven decisions that reduce risks and maximize
            opportunities.
          </p>
        </div>
        <div className="mk-da-benefit-item">
          <h3>Enhanced Operational Efficiency</h3>
          <p>
            Identify bottlenecks and optimize processes for better
            performance.
          </p>
        </div>
        <div className="mk-da-benefit-item">
          <h3>Competitive Advantage</h3>
          <p>
            Gain insights that your competitors might miss, putting you
            ahead in the market.
          </p>
        </div>
        <div className="mk-da-benefit-item">
          <h3>Customer Insights</h3>
          <p>
            Understand your customers better to improve satisfaction and
            retention.
          </p>
        </div>
        {/* Two new benefit items added below */}
        <div className="mk-da-benefit-item">
          <h3>Predictive Analytics</h3>
          <p>
            Forecast future trends and behaviors to proactively address 
            challenges and opportunities.
          </p>
        </div>
        <div className="mk-da-benefit-item">
          <h3>Cost Reduction</h3>
          <p>
            Identify areas of wasteful spending and optimize resource 
            allocation for maximum ROI.
          </p>
        </div>
      </div>
    </section>

        <section className="mk-da-cta-section">
          <div className="mk-da-cta-container">
            <h2>Ready to Unlock the Power of Your Data?</h2>
            <p>
              Contact us today for a free consultation and discover how our data
              analytics services can transform your business.
            </p>
            <a
              href="https://wa.me/919155599968?text=Hi!%20I%20am%20interested%20in%20Data%20Analytics%20services%20such%20as%20business%20intelligence%2C%20data%20visualization%2C%20and%20predictive%20analytics.%20Please%20schedule%20a%20consultation%20with%20your%20experts."
              target="_blank"
              rel="noreferrer"
            >
              <button className="mk-da-cta-btn">Schedule a Consultation</button>
            </a>
          </div>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataAnalytics;
